import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import Index from "./pages/Index";
import About from "./pages/About";
import Services from "./pages/Services";
import Fleet from "./pages/Fleet";
import FleetBrochure from "./pages/FleetBrochure";
import Operations from "./pages/Operations";

import Safety from "./pages/Safety";
import Contact from "./pages/Contact";
import ClientLogin from "./pages/ClientLogin";
import Dashboard from "./pages/portal/Dashboard";
import Documents from "./pages/portal/Documents";
import Questions from "./pages/portal/Questions";
import Progress from "./pages/portal/Progress";
import PortalFleet from "./pages/portal/Fleet";
import PortalNews from "./pages/portal/News";
import VoyageOperations from "./pages/portal/VoyageOperations";
import HSEQCompliance from "./pages/portal/HSEQCompliance";
import CommercialFinance from "./pages/portal/CommercialFinance";
import ClientSatisfaction from "./pages/portal/ClientSatisfaction";
import CrewPersonnel from "./pages/portal/CrewPersonnel";
import ESGEmissions from "./pages/portal/ESGEmissions";
import TrainingCompetency from "./pages/portal/TrainingCompetency";
import EmergencySAR from "./pages/portal/EmergencySAR";
import MaintenancePMS from "./pages/portal/MaintenancePMS";
import MessagesNotices from "./pages/portal/MessagesNotices";
import ReportsAnalytics from "./pages/portal/ReportsAnalytics";
import PortalAdmin from "./pages/portal/PortalAdmin";
import LessonsLearned from "./pages/portal/LessonsLearned";
import PortalCrewTransferIntelligence from "./pages/portal/CrewTransferIntelligence";
import EmployeeCrewTransferIntelligence from "./pages/employee/EmployeeCrewTransferIntelligence";
import CommercialValueCenter from "./pages/portal/CommercialValueCenter";
import PortalChemCarriage from "./pages/portal/ChemCarriage";
import EmployeeCommercialValueCenter from "./pages/employee/EmployeeCommercialValueCenter";
import EmployeeChemCarriage from "./pages/employee/EmployeeChemCarriage";
import EarMarList from "./pages/employee/earmar/EarMarList";
import EarMarNew from "./pages/employee/earmar/EarMarNew";
import EarMarDetail from "./pages/employee/earmar/EarMarDetail";
import AdminEarMarApprovers from "./pages/admin/AdminEarMarApprovers";
import ResetPassword from "./pages/ResetPassword";
import ChangePassword from "./pages/ChangePassword";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminCompanies from "./pages/admin/AdminCompanies";
import AdminFleetAssignment from "./pages/admin/AdminFleetAssignment";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminDocuments from "./pages/admin/AdminDocuments";
import AdminQuestions from "./pages/admin/AdminQuestions";
import AdminProgress from "./pages/admin/AdminProgress";
import AdminEmployees from "./pages/admin/AdminEmployees";
import AdminApprovals from "./pages/admin/AdminApprovals";
import EmployeeDashboard from "./pages/employee/EmployeeDashboard";
import EmployeeDocuments from "./pages/employee/EmployeeDocuments";
import EmployeeQuestions from "./pages/employee/EmployeeQuestions";
import EmployeeProgress from "./pages/employee/EmployeeProgress";
import EmployeeAnnouncements from "./pages/employee/EmployeeAnnouncements";
import EmployeeCrewRoster from "./pages/employee/EmployeeCrewRoster";
import EmployeeSafetyBulletins from "./pages/employee/EmployeeSafetyBulletins";
import EmployeeDirectory from "./pages/employee/EmployeeDirectory";
import EmployeeFleet from "./pages/employee/EmployeeFleet";
import EmployeeTraining from "./pages/employee/EmployeeTraining";
import EmployeeCompliance from "./pages/employee/EmployeeCompliance";
import EmployeeContracts from "./pages/employee/EmployeeContracts";
import EmployeeEmergencyContacts from "./pages/employee/EmployeeEmergencyContacts";
import EmployeeVendors from "./pages/employee/EmployeeVendors";
import EmployeeOnboarding from "./pages/employee/EmployeeOnboarding";
import EmployeeAccountSettings from "./pages/employee/EmployeeAccountSettings";
import EmployeeSupplyChain from "./pages/employee/EmployeeSupplyChain";
import EmployeeFleetIntelligence from "./pages/employee/EmployeeFleetIntelligence";
import EmployeeReadingTimeReport from "./pages/employee/EmployeeReadingTimeReport";
import EmployeeSatisfaction from "./pages/employee/EmployeeSatisfaction";
import EmployeeVoyageOperations from "./pages/employee/EmployeeVoyageOperations";
import EmployeeCrewPersonnel from "./pages/employee/EmployeeCrewPersonnel";
import EmployeeCommercialFinance from "./pages/employee/EmployeeCommercialFinance";
import EmployeeESGEmissions from "./pages/employee/EmployeeESGEmissions";
import EmployeeReportsAnalytics from "./pages/employee/EmployeeReportsAnalytics";
import EmployeeMaintenancePMS from "./pages/employee/EmployeeMaintenancePMS";
import EmployeeLessonsLearned from "./pages/employee/EmployeeLessonsLearned";
import EmployeeVesselCatalogue from "./pages/employee/EmployeeVesselCatalogue";
import DocumentController from "./pages/employee/DocumentController";
import CrewScheduler from "./pages/employee/CrewScheduler";
import RateIntelligence from "./pages/employee/RateIntelligence";
import IntelligenceDigest from "./pages/employee/IntelligenceDigest";
import FinancialPulse from "./pages/employee/FinancialPulse";
import PartnerHub from "./pages/employee/PartnerHub";
import ContractRedline from "./pages/employee/ContractRedline";
import RegulatoryRadar from "./pages/employee/RegulatoryRadar";
import MeetingPrep from "./pages/employee/MeetingPrep";
import ClauseLibrary from "./pages/employee/ClauseLibrary";
import ClientOnboarding from "./pages/employee/ClientOnboarding";
import PMSAssistant from "./pages/employee/PMSAssistant";
import DefectManager from "./pages/employee/DefectManager";
import WeatherRouting from "./pages/employee/WeatherRouting";
import TrainingNeeds from "./pages/employee/TrainingNeeds";
import CrewWelfare from "./pages/employee/CrewWelfare";
import HRGenerator from "./pages/employee/HRGenerator";
import BoardReport from "./pages/employee/BoardReport";
import OVMSAAssessment from "./pages/employee/OVMSAAssessment";
import AuditPrep from "./pages/employee/AuditPrep";
import MaintenanceHub from "./pages/employee/MaintenanceHub";
import DocumentsHub from "./pages/employee/DocumentsHub";
import CommercialHub from "./pages/employee/CommercialHub";
import MaintenancePMSHub from "./pages/employee/MaintenancePMSHub";
import DocumentsSOPsHub from "./pages/employee/DocumentsSOPsHub";
import VoyageManagement from "./pages/employee/VoyageManagement";
import AdminSupplyChain from "./pages/admin/AdminSupplyChain";
import AdminCrewIntelligence from "./pages/admin/AdminCrewIntelligence";
import AdminBackend from "./pages/admin/AdminBackend";
import AdminPMSBuilder from "./pages/admin/AdminPMSBuilder";
import AdminTankLevels from "./pages/admin/AdminTankLevels";
import PortalAccountSettings from "./pages/portal/AccountSettings";
import EmployeeLogin from "./pages/EmployeeLogin";
import SEOHead from "./components/SEOHead";
import NotFound from "./pages/NotFound";
import BrandAssets from "./pages/BrandAssets";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Cookies from "./pages/Cookies";
import CookieConsent from "./components/CookieConsent";
import AdminAuditors from "./pages/admin/AdminAuditors";
import AuditorLibrary from "./pages/auditor/AuditorLibrary";
import AuditorDocumentViewer from "./pages/auditor/AuditorDocumentViewer";
import InternalAudits from "./pages/employee/InternalAudits";
import AuditorLogin from "./pages/AuditorLogin";
import AuditorRoom from "./pages/auditor/AuditorRoom";
import EmployeeAuditRoom from "./pages/employee/EmployeeAuditRoom";
import RouteErrorBoundary from "./components/RouteErrorBoundary";
// Sentinel (military) portal hidden — keep imports out of bundle.

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});


const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
          <AuthProvider>
            <SEOHead />
            <ScrollToTop />
            <CookieConsent />
            <RouteErrorBoundary>
            <Routes>

            <Route path="/" element={<Index />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/fleet" element={<Fleet />} />
            <Route path="/fleet-brochure" element={<FleetBrochure />} />
            <Route path="/fleet-brochure/:vesselId" element={<FleetBrochure />} />
            <Route path="/operations" element={<Operations />} />
            
            <Route path="/safety" element={<Safety />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/brand" element={<BrandAssets />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/client-login" element={<ClientLogin />} />
            <Route path="/portal" element={<Dashboard />} />
            <Route path="/portal/fleet" element={<PortalFleet />} />
            <Route path="/portal/documents" element={<Documents />} />
            <Route path="/portal/questions" element={<Questions />} />
            <Route path="/portal/progress" element={<Progress />} />
            <Route path="/portal/news" element={<PortalNews />} />
            <Route path="/portal/voyages" element={<VoyageOperations />} />
            <Route path="/portal/hseq" element={<HSEQCompliance />} />
            <Route path="/portal/finance" element={<CommercialFinance />} />
            <Route path="/portal/satisfaction" element={<ClientSatisfaction />} />
            <Route path="/portal/crew" element={<CrewPersonnel />} />
            <Route path="/portal/esg" element={<ESGEmissions />} />
            <Route path="/portal/training" element={<TrainingCompetency />} />
            <Route path="/portal/emergency" element={<EmergencySAR />} />
            <Route path="/portal/maintenance" element={<MaintenancePMS />} />
            <Route path="/portal/messages" element={<MessagesNotices />} />
            <Route path="/portal/reports" element={<ReportsAnalytics />} />
            <Route path="/portal/admin" element={<PortalAdmin />} />
            <Route path="/portal/lessons-learned" element={<LessonsLearned />} />
            <Route path="/portal/crew-transfer-intelligence" element={<PortalCrewTransferIntelligence />} />
            <Route path="/portal/value-center" element={<CommercialValueCenter />} />
            <Route path="/portal/chem-carriage" element={<PortalChemCarriage />} />
            <Route path="/employee/value-center" element={<EmployeeCommercialValueCenter />} />
            <Route path="/employee/chem-carriage" element={<EmployeeChemCarriage />} />
            <Route path="/employee/ear-mar" element={<EarMarList />} />
            <Route path="/employee/ear-mar/new" element={<EarMarNew />} />
            <Route path="/employee/ear-mar/:id" element={<EarMarDetail />} />
            <Route path="/admin/ear-mar-approvers" element={<AdminEarMarApprovers />} />

            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/change-password" element={<ChangePassword />} />
            {/* Admin routes */}
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/approvals" element={<AdminApprovals />} />
            <Route path="/admin/companies" element={<AdminCompanies />} />
            <Route path="/admin/fleet-assignment" element={<AdminFleetAssignment />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/employees" element={<AdminEmployees />} />
            <Route path="/admin/documents" element={<AdminDocuments />} />
            <Route path="/admin/questions" element={<AdminQuestions />} />
            <Route path="/admin/progress" element={<AdminProgress />} />
            <Route path="/admin/supply-chain" element={<AdminSupplyChain />} />
            <Route path="/admin/crew-intelligence" element={<AdminCrewIntelligence />} />
            <Route path="/admin/backend" element={<AdminBackend />} />
            <Route path="/admin/pms-builder" element={<AdminPMSBuilder />} />
            <Route path="/admin/tank-levels" element={<AdminTankLevels />} />
            {/* Employee routes */}
            <Route path="/employee-login" element={<EmployeeLogin />} />
            <Route path="/employee" element={<EmployeeDashboard />} />
            <Route path="/employee/announcements" element={<EmployeeAnnouncements />} />
            <Route path="/employee/crew-roster" element={<EmployeeCrewRoster />} />
            <Route path="/employee/safety" element={<EmployeeSafetyBulletins />} />
            <Route path="/employee/directory" element={<EmployeeDirectory />} />
            <Route path="/employee/documents" element={<DocumentsSOPsHub />} />
            <Route path="/employee/voyage-management" element={<VoyageManagement />} />
            <Route path="/employee/questions" element={<EmployeeQuestions />} />
            <Route path="/employee/progress" element={<EmployeeProgress />} />
            <Route path="/employee/fleet" element={<EmployeeFleet />} />
            <Route path="/employee/training" element={<EmployeeTraining />} />
            <Route path="/employee/compliance" element={<EmployeeCompliance />} />
            <Route path="/employee/contracts" element={<EmployeeContracts />} />
            <Route path="/employee/emergency-contacts" element={<EmployeeEmergencyContacts />} />
            <Route path="/employee/vendors" element={<EmployeeVendors />} />
            <Route path="/employee/onboarding" element={<EmployeeOnboarding />} />
            <Route path="/employee/settings" element={<EmployeeAccountSettings />} />
            <Route path="/employee/supply-chain" element={<EmployeeSupplyChain />} />
            <Route path="/employee/fleet-intelligence" element={<EmployeeFleetIntelligence />} />
            <Route path="/employee/reading-analytics" element={<EmployeeReadingTimeReport />} />
            <Route path="/employee/satisfaction" element={<EmployeeSatisfaction />} />
            <Route path="/employee/voyage-operations" element={<EmployeeVoyageOperations />} />
            <Route path="/employee/crew-personnel" element={<EmployeeCrewPersonnel />} />
            <Route path="/employee/commercial-finance" element={<EmployeeCommercialFinance />} />
            <Route path="/employee/esg-emissions" element={<EmployeeESGEmissions />} />
            <Route path="/employee/reports-analytics" element={<EmployeeReportsAnalytics />} />
            <Route path="/employee/maintenance" element={<MaintenancePMSHub />} />
            <Route path="/employee/lessons-learned" element={<EmployeeLessonsLearned />} />
            <Route path="/employee/vessel-catalogue" element={<EmployeeVesselCatalogue />} />
            <Route path="/employee/document-controller" element={<DocumentController />} />
            <Route path="/employee/crew-scheduler" element={<CrewScheduler />} />
            <Route path="/employee/rate-intelligence" element={<RateIntelligence />} />
            <Route path="/employee/crew-transfer-intelligence" element={<EmployeeCrewTransferIntelligence />} />
            <Route path="/employee/intelligence-digest" element={<IntelligenceDigest />} />
            <Route path="/employee/financial-pulse" element={<FinancialPulse />} />
            <Route path="/employee/partner-hub" element={<PartnerHub />} />
            <Route path="/employee/contract-redline" element={<ContractRedline />} />
            <Route path="/employee/regulatory-radar" element={<RegulatoryRadar />} />
            <Route path="/employee/meeting-prep" element={<MeetingPrep />} />
            <Route path="/employee/clause-library" element={<ClauseLibrary />} />
            <Route path="/employee/client-onboarding" element={<ClientOnboarding />} />
            <Route path="/employee/pms-assistant" element={<PMSAssistant />} />
            <Route path="/employee/defect-manager" element={<DefectManager />} />
            <Route path="/employee/weather-routing" element={<WeatherRouting />} />
            <Route path="/employee/training-needs" element={<TrainingNeeds />} />
            <Route path="/employee/crew-welfare" element={<CrewWelfare />} />
            <Route path="/employee/hr-generator" element={<HRGenerator />} />
            <Route path="/employee/board-report" element={<BoardReport />} />
            <Route path="/employee/ovmsa-assessment" element={<OVMSAAssessment />} />
            <Route path="/employee/audit-prep" element={<AuditPrep />} />
            <Route path="/employee/maintenance-hub" element={<MaintenanceHub />} />
            <Route path="/employee/documents-hub" element={<DocumentsHub />} />
            <Route path="/employee/commercial-hub" element={<CommercialHub />} />
            <Route path="/portal/settings" element={<PortalAccountSettings />} />
            {/* Auditor portal (Marshall Islands flag state) — read-only Documents & SOPs only */}
            <Route path="/auditor-login" element={<AuditorLogin />} />
            <Route path="/auditor" element={<AuditorLibrary />} />
            <Route path="/auditor/library" element={<AuditorLibrary />} />
            <Route path="/auditor/audit-room" element={<AuditorRoom />} />
            <Route path="/auditor/document/:id" element={<AuditorDocumentViewer />} />
            <Route path="/employee/audit-room" element={<EmployeeAuditRoom />} />
            <Route path="/employee/internal-audits" element={<InternalAudits />} />
            <Route path="/admin/auditors" element={<AdminAuditors />} />
            {/* Sentinel — Joint Military Portal (hidden) */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </RouteErrorBoundary>
        </AuthProvider>

      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;

